import { useEffect, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Page, PageTitleWithBack, Table, Pagination, Loader } from '../../../components';
import { Button, Grid, Divider, Theme, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Edit, Delete, Add } from '@mui/icons-material';
import { getTerritories, getTerritory, deleteTerritory, getTerritoryImages } from '../../../fetch';
import { ITerritory, ITerritoryDetail } from '../../../models';
import { TerritoryModal } from './TerritoryModal';
import { MobileTerritory } from './MobileTerritory';

export const ManageTerritories = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [territores, setTerritores] = useState<ITerritory[]>([]);
  const [isLoadingTerritories, setLoadingTerritories] = useState<boolean>(true);
  const [isModalOpen, showTerritoriesModal] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [currentTerritory, setCurrrentTerritory] = useState<ITerritoryDetail>(null);
  const [loadingTerritory, setLoadingTerritory] = useState(false);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const classes = useStyles();

  const fetchTerritories = async () => {
    try {
      setLoadingTerritories(true);
      const res = await getTerritories({ page: page + 1, perPage: perPage });
      setRecordCount(res.totalRecordCount);
      setTerritores(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading territories, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingTerritories(false);
    }
  };

  useEffect(() => {
    fetchTerritories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const handleEdit = async (id: number) => {
    setLoadingTerritory(true);
    let territory: ITerritoryDetail | null = null;
    try {
      territory = await getTerritory(id);
      const res = await getTerritoryImages(id);
      const filteredRes = res.filter(image => image !== null);
      setCurrrentTerritory({
        ...territory,
        images: filteredRes ?? []
      });
      showTerritoriesModal(true);
    } catch (error) {
      // images failed to load, still load the modal and set the images array to empty
      if (error?.Detail?.includes('Legacy System')) {
        setCurrrentTerritory({
          ...territory,
          images: []
        });
        showTerritoriesModal(true);
      } else {
        enqueueSnackbar(error?.Detail ?? 'Error loading territory, please try again', {
          variant: 'error'
        });
      }
    } finally {
      setLoadingTerritory(false);
    }
  };

  const handleDelete = async id => {
    const result = window.confirm('Are you sure you want to delete this Territory?');
    if (result) {
      setDeleting(true);
      try {
        await deleteTerritory(id);
        fetchTerritories();
        enqueueSnackbar('Territory Deleted!', {
          variant: 'success'
        });
      } catch (error) {
        var errorMessage = error?.response?.data?.Detail;
        if (errorMessage) {
          enqueueSnackbar(errorMessage, {
            variant: 'error'
          });
        } else {
          enqueueSnackbar('Error, unable to delete territory, please try again', {
            variant: 'error'
          });
        }
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Manager',
        accessor: 'manager',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ITerritory } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  handleEdit(original.territoryId);
                }}
              >
                Edit
              </Button>
              <Button
                className={classes.deleteButton}
                startIcon={<Delete />}
                onClick={() => {
                  handleDelete(original.territoryId);
                }}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territores]);
  return (
    <>
      {(loadingTerritory || isDeleting) && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Manage Territories">
        <Grid container>
          <Grid item xs={12} className={classes.pageTitleWrapper}>
            <PageTitleWithBack
              showDivider={false}
              backText="Back to Admin home"
              backUrl="/admin"
              title="Manage Territories"
            />
          </Grid>
          <Grid item className={classes.infoWrapper}>
            <Grid item xs={12}>
              <Button
                id="add-territory"
                onClick={() => {
                  showTerritoriesModal(true);
                }}
                startIcon={<Add />}
                color="primary"
                variant="contained"
              >
                Add Territory
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        <Table
          columns={columns}
          data={territores}
          isLoading={isLoadingTerritories}
          hidePagination
          mobileProps={{
            handleEdit: (val: ITerritory) => handleEdit(val.territoryId),
            handleDelete: val => handleDelete(val.territoryId)
          }}
          ResponsiveComponent={isMobile ? MobileTerritory : undefined}
        />
        {!isLoadingTerritories && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}

        <TerritoryModal
          currentTerritory={currentTerritory}
          open={isModalOpen}
          onClose={() => {
            showTerritoriesModal(false);
            setTimeout(() => {
              setCurrrentTerritory(null);
            }, 500);
          }}
          fetchTerritories={fetchTerritories}
        />
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  addNeighborhoodWrapper: {
    marginBottom: theme.spacing(1)
  },
  addNeighborhoodButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },

  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  actionButtons: {
    display: 'flex',
    textAlign: 'right'
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
